/**
 * Admin Consent URL Generation Utilities
 *
 * Utilities for generating Azure AD admin consent URLs for multi-tenant access.
 */

/**
 * Generate admin consent URL for a specific tenant
 *
 * When a tenant admin visits this URL, they will be prompted to grant consent
 * for the application to access their tenant's Microsoft Graph API.
 *
 * @param tenantId - Azure AD tenant ID (GUID)
 * @param clientId - Azure AD application client ID
 * @param backendUrl - Backend API base URL for redirect callback
 * @returns Full admin consent URL
 *
 * @example
 * ```typescript
 * const url = generateAdminConsentUrl(
 *   '12345678-1234-1234-1234-123456789abc',
 *   'ad17160a-05fb-4faf-8b7b-13916f30e55e',
 *   'http://localhost:9000'
 * );
 * // Returns: https://login.microsoftonline.com/{tenant-id}/adminconsent?client_id=...&redirect_uri=...
 * ```
 */
export function generateAdminConsentUrl(
    tenantId: string,
    clientId: string,
    backendUrl: string
): string {
    const baseUrl = backendUrl || window.location.origin;
    const redirectUri = `${baseUrl}/api/auth/admin-consent-callback`;

    const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
    });

    return `https://login.microsoftonline.com/${tenantId}/adminconsent?${params.toString()}`;
}
